import { Listing } from "../../../types/listings.types";
import { baseApi } from "../app-main/store/baseApi";

export type OwnerListingImage = {
  /** Legacy v1 shape: Cloudinary public id used to build a transform URL. */
  external_id?: string;
  /** v2 `spaces-v2` shape: ready-to-use Cloudinary image URL. */
  url?: string;
  /** v2 `spaces-v2` shape: optional pre-sized thumbnail URL. */
  thumbnail_url?: string;
};

export type OwnerListing = {
  _id: string;
  title?: string;
  use_type?: string;
  status?: string;
  /** Denormalized from the host's profile; consistent across all listings. */
  is_instant_book_eligible_host?: boolean;
  /** Per listing: whether the host has turned Instant Book on for it. */
  is_instant_book_enabled?: boolean;
  /** Per listing: true only when both eligible and enabled. */
  is_instant_book_active?: boolean;
  images?: OwnerListingImage[];
  metro?: string;
  /** ISO country code, e.g. "US". */
  country?: string;
  parentSpaceId?: string;
  /** Present only for Full-Service Venue listings; absence means non-FSV. */
  full_service?: Record<string, unknown>;
};

export type ListingUseType = "Event" | "Off-Site" | "Production";

/**
 * The subset of a listing's fields this app edits via `PUT /v1/listings/:id`.
 */
export type UpdateListingPayload = Partial<
  Pick<
    Listing,
    | "title"
    | "description"
    | "rules"
    | "min_age"
    | "minimum_duration"
    | "number_guests"
  >
>;

export type UpdateListingArgs = {
  listingId: string;
  payload: UpdateListingPayload;
};

export type UpdateListingAmenitiesArgs = {
  /** The listing whose amenities are being replaced. */
  parentId: string;
  ownerId: string;
  amenities: { name: string }[];
};

/**
 * Body for `PUT /spaces-v2/listings/fsv/:listing-id`. Every key is
 * optional; send only what changed.
 */
export type FsvListingUpdatePayload = {
  full_service?: {
    room_type?: NonNullable<Listing["full_service"]>["room_type"];
    gratuity_percent?: number;
    menu_url?: string;
  };
  /** The F&B minimum inventory item. */
  included_addon?: { unit_cost?: number };
  /** The space rental; 0 means no space rental. */
  flat_rate_price_value?: number;
};

export type UpdateFsvListingArgs = {
  listingId: string;
  payload: FsvListingUpdatePayload;
};

/** Body for `POST /spaces-v2/listings/fsv/:listing-id/validate-pricing`. */
export type FsvPricingValidationPayload = {
  /** The space rental; 0 means no space rental. */
  flat_rate_price_value: number;
  /** The F&B minimum. */
  included_addon: { unit_cost: number };
};

export type FsvPricingValidationResponse = { valid: true };

/**
 * The 400 body for invalid FSV pricing, from both `validate-pricing` and the
 * FSV update.
 */
export type FsvPricingErrorBody = {
  error_code?: string;
  /** The lowest allowed space rental; sent with `flat_rate_below_floor`. */
  space_rental_amount_min?: number;
  /** The lowest allowed F&B minimum + space rental; sent with `invalid_combined_minimum`. */
  combined_amount_min?: number;
  /** The lowest allowed F&B minimum with no space rental; sent with `invalid_f_and_b_min`. */
  f_and_b_amount_min?: number;
};

export type ValidateFsvPricingArgs = {
  listingId: string;
  payload: FsvPricingValidationPayload;
};

export type CleaningFeeValue = {
  obj_state: "ACTIVATED" | "DEACTIVATED";
  value: number;
  apply_as: "FLAT_RATE" | "INCLUDED";
};

export type DayRateDiscountValue = {
  obj_state: "ACTIVATED" | "DEACTIVATED";
  value: number;
  apply_as: "FLAT_RATE" | "PERCENT_OFF";
};

/**
 * Payload for `POST /v1/listings/space/:spaceId`
 */
export type CreateListingPayload = {
  status: "PENDING";
  use_name: ListingUseType;
  amenities: string[];
  price_currency: string;
  duration_type: "HOURLY_RATE";
  price_value: string;
  minimum_duration: string;
  number_guests: string;
  cleaning_fee: CleaningFeeValue;
  day_rate_discount: DayRateDiscountValue;
  is_instant_book_enabled: boolean;
};

export type CreateListingArgs = {
  spaceId: string;
  payload: CreateListingPayload;
};

export type CreateListingResponse = {
  _id: string;
  use_name: ListingUseType;
};

export type OwnerListingsResponse = {
  listings: OwnerListing[];
  total_count: number;
};

export type GetOwnerListingsArgs = {
  ssoId: string;
};

/**
 * Listings owned by a host. Backed by
 * `GET /v2/spaces-v2/listings/owner/:sso-id`. `full=true` returns the complete
 * listing payload (including instant-book eligibility flags) and
 * `status=activated` keeps the result to listings the host can still act on.
 * `subresources=images` is required for `spaces-v2` to include listing images
 * (the v2 endpoint does not return them via `full` alone).
 */
export const listingsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOwnerListings: builder.query<
      OwnerListingsResponse,
      GetOwnerListingsArgs
    >({
      query: ({ ssoId }) => ({
        url: `/v2/spaces-v2/listings/owner/${ssoId}`,
        params: {
          full: true,
          limit: 400,
          subresources: "images",
        },
      }),
    }),

    /**
     * Listings Peerspace has paused internally (`status: "PAUSED_INTERNALLY"`)
     * for a host. Reuses the same owner-listings endpoint as `getOwnerListings`
     * with a `PAUSED_INTERNALLY` status filter; there's no dedicated endpoint and
     * no pause-reason enrichment (the Host Hub card copy is static). The result
     * is filtered again client-side to be defensive about the status param.
     */
    getPausedInternallyListings: builder.query<
      OwnerListingsResponse,
      GetOwnerListingsArgs
    >({
      query: ({ ssoId }) => ({
        url: `/v2/spaces-v2/listings/owner/${ssoId}`,
        params: {
          full: true,
          status: "PAUSED_INTERNALLY",
          limit: 400,
          subresources: "images",
        },
      }),
    }),

    /**
     * Fetches only listings that are not deactivated (`status: "DEACTIVATED"`).
     */
    getNonDeactivatedListings: builder.query<
      OwnerListingsResponse,
      GetOwnerListingsArgs
    >({
      query: ({ ssoId }) => ({
        url: `/v2/spaces-v2/listings/owner/${ssoId}`,
        params: {
          full: true,
          no_deactivated: true,
          limit: 400,
        },
      }),
    }),

    /**
     * Activates a listing use type for a space
     */
    createListing: builder.mutation<CreateListingResponse, CreateListingArgs>({
      query: ({ spaceId, payload }) => ({
        url: `/v1/listings/space/${spaceId}`,
        method: "POST",
        body: payload,
      }),
    }),

    getListing: builder.query<Listing, { listingId: string }>({
      query: ({ listingId }) => `/v1/listings/${listingId}`,
    }),

    /**
     * A listing with the subresources the FSV page needs. Calls spaces-v2
     * directly because `/v1/listings/:id` proxies with a fixed subresource list
     * that excludes `inventory`.
     */
    getFsvListing: builder.query<Listing, { listingId: string }>({
      // Repeated keys, not `params`: fetchBaseQuery joins arrays with commas,
      // which spaces-v2 reads as a single (invalid) subresource name.
      query: ({ listingId }) =>
        `/v2/spaces-v2/listings/${listingId}?subresources=prices&subresources=inventory`,
    }),

    /**
     * Generic partial update for a listing. Every edit-listing section saves
     * through this same endpoint with only the fields it owns.
     */
    updateListing: builder.mutation<Listing, UpdateListingArgs>({
      query: ({ listingId, payload }) => ({
        url: `/v1/listings/${listingId}`,
        method: "PUT",
        body: payload,
      }),
    }),

    /**
     * Updates the FSV data on an FSV Event listing (full_service, F&B
     * minimum, space rental). spaces-v2 validates the space rental against
     * the F&B tier floor and returns a 400 with a pricing `error_code`.
     */
    updateFsvListing: builder.mutation<unknown, UpdateFsvListingArgs>({
      query: ({ listingId, payload }) => ({
        url: `/v2/spaces-v2/listings/fsv/${listingId}`,
        method: "PUT",
        body: payload,
      }),
    }),

    /**
     * Checks an FSV space rental + F&B minimum against the listing's pricing
     * rules without saving. Invalid pricing (or a failed fees lookup) is a 400
     * with an `FsvPricingErrorBody`.
     */
    validateFsvPricing: builder.mutation<
      FsvPricingValidationResponse,
      ValidateFsvPricingArgs
    >({
      query: ({ listingId, payload }) => ({
        url: `/v2/spaces-v2/listings/fsv/${listingId}/validate-pricing`,
        method: "POST",
        body: payload,
      }),
    }),

    /**
     * Replaces a listing's full amenities list in one call. Backed by
     * `POST /listings/amenities/batch`, which deletes every existing amenity
     * for `parentId` and recreates the given array — a full replace, not a
     * diff, so every amenity (including unchanged ones) gets a new `_id`.
     */
    updateListingAmenities: builder.mutation<
      unknown,
      UpdateListingAmenitiesArgs
    >({
      query: (payload) => ({
        url: `/v1/listings/amenities/batch`,
        method: "POST",
        body: payload,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetOwnerListingsQuery,
  useGetPausedInternallyListingsQuery,
  useGetNonDeactivatedListingsQuery,
  useCreateListingMutation,
  useGetListingQuery,
  useGetFsvListingQuery,
  useUpdateListingMutation,
  useUpdateListingAmenitiesMutation,
  useUpdateFsvListingMutation,
  useValidateFsvPricingMutation,
} = listingsApi;
